import { useMemo } from "react"

import { CheckCircledIcon } from "@radix-ui/react-icons"

import ActionButton from "./ActionButton/ActionButton"
import {
	canEdit,
	claimingConversionExplainMessage,
	formatMenusAndCoupons,
	getRereservationOriginalPrice,
	getSpanClass,
	getStatusDisplayText,
} from "./CancelsForRereservationTableView.logic"
import styles from "./CancelsForRereservationTableView.module.scss"
import {
	isClaimingConversion,
	renderVisitCount,
} from "./CancelsTableView.common.logic"

import QuestionExplanationIcon from "@/lib/components/ui/QuetionExplanationIcon/QuestionExplanationIcon"
import { CancelForRereservationFragment } from "@/main/gql/__generated__/graphql"
import { useCancelTypeInfos } from "@/main/lib/useCancelTypeInfos"
import {
	convertGraphQLDateToDate,
	formatJstDateUntilDayByHyphen,
} from "@/utils/date"

import { EditingRereservationRow } from "../HomePage.logic"

export type Props = {
	currentTab:
		| "ENCOURAGING_RERESERVATION_NOT_SCHEDULED"
		| "ENCOURAGING_RERESERVATION_SCHEDULED"
		| "RERESERVED"
	cancels: readonly CancelForRereservationFragment[]
	editingRereservationRowMap: Record<string, EditingRereservationRow> // key is idv2
	updateEditingRereservationRow: (
		idv2: string,
		row: EditingRereservationRow
	) => void
	onClickUpdateButton: (idv2: string) => void
	onClickActionButton: (idv2: string) => void
	onClickRereservationRowEdit: (idv2: string) => void
	cancelRereservationRowEdit: (idv2: string) => void
}

type Header = {
	title: string[]
	explainMessage?: string
}

const tableHeadersForNotScheduled: Header[] = [
	{ title: ["ステータス"] },
	{
		title: ["請求転換"],
		explainMessage: claimingConversionExplainMessage,
	},
	{ title: ["キャンセル", "種別"] },
	{ title: ["キャンセル", "処理日"] },
	{ title: ["来店回数"] },
	{ title: ["お客様"] },
	{ title: ["電話番号"] },
	{ title: ["アクション"] },
	{ title: ["編集"] },
]

const tableHeadersForScheduled: Header[] = [
	{ title: ["ステータス"] },
	{ title: ["請求転換"], explainMessage: claimingConversionExplainMessage },
	{ title: ["キャンセル", "種別"] },
	{ title: ["キャンセル", "処理日"] },
	{ title: ["来店回数"] },
	{ title: ["お客様"] },
	{ title: ["電話番号"] },
	{ title: ["アクション"] },
]

// 再予約金額セル: design 参照 frontend/design/store_management/rereservation_rereserved_design.png
// discountPrice が non-null の時は元金額 (店舗の成果額) を太字で表示し、
// 下に RefundHub割引バッジと割引額 + 割引後金額を補足する.
type RereservationPriceCellProps = {
	rereservation: {
		price: number
		discountPrice: number | null
	} | null
}

const RereservationPriceCell = ({
	rereservation,
}: RereservationPriceCellProps) => {
	if (!rereservation) {
		return null
	}
	const discountedPrice = rereservation.price
	const discount = rereservation.discountPrice
	if (discount == null || discount === 0) {
		return <span className={styles.price_main}>¥{discountedPrice}</span>
	}
	const originPrice = getRereservationOriginalPrice(rereservation)
	return (
		<div className={styles.price_cell}>
			<span className={styles.price_main}>¥{originPrice}</span>
			<div className={styles.discount_row}>
				<span className={styles.discount_badge}>
					<span className={styles.discount_badge_text}>RefundHub割引</span>
				</span>
				<span className={styles.discount_text}>
					-¥{discount}（割引後 ¥{discountedPrice}）
				</span>
			</div>
		</div>
	)
}

const tableHeadersForRereserved: Header[] = [
	{ title: ["ステータス"] },
	{ title: ["請求転換"], explainMessage: claimingConversionExplainMessage },
	{ title: ["キャンセル", "種別"] },
	{ title: ["キャンセル", "処理日"] },
	{ title: ["来店回数"] },
	{ title: ["再予約ID"] },
	{ title: ["再予約金額"] },
	{ title: ["施術時間"] },
	{ title: ["施術メニュー"] },
	{ title: ["再来店予定日"] },
	{ title: ["担当スタッフ"] },
	{ title: ["お客様"] },
	{ title: ["電話番号"] },
]

export const CancelsForRereservationTableView = ({
	currentTab,
	cancels,
	editingRereservationRowMap,
	updateEditingRereservationRow,
	onClickUpdateButton,
	onClickActionButton,
	onClickRereservationRowEdit,
	cancelRereservationRowEdit,
}: Props) => {
	const { getCancelTypeLabel } = useCancelTypeInfos()
	const isEditingRow = (idv2: string): boolean => {
		return idv2 in editingRereservationRowMap
	}

	const tableHeaders = useMemo(() => {
		if (currentTab === "ENCOURAGING_RERESERVATION_NOT_SCHEDULED") {
			return tableHeadersForNotScheduled
		} else if (currentTab === "ENCOURAGING_RERESERVATION_SCHEDULED") {
			return tableHeadersForScheduled
		}
		return tableHeadersForRereserved
	}, [currentTab])

	return (
		<div>
			<div className={styles.table_container}>
				<table className={styles.scroll_table}>
					<thead>
						<tr>
							{tableHeaders.map((header: Header, index) => {
								return (
									<th key={index}>
										{header.title.map((text, i) => {
											if (header.explainMessage) {
												return (
													<div key={i} className={styles.question_section}>
														<span>{text}</span>
														<span className={styles.question_hover_card}>
															<QuestionExplanationIcon
																contentMessage={header.explainMessage}
															/>
														</span>
													</div>
												)
											} else {
												return <div key={i}>{text}</div>
											}
										})}
									</th>
								)
							})}
						</tr>
					</thead>
					<tbody>
						{cancels.map((row, _) => (
							<tr key={row.idv2}>
								<td key={`${row.idv2}-status`}>
									<span
										className={`${getSpanClass(row, styles)} ${
											styles.table_elm_background
										}`}
									>
										{getStatusDisplayText(row)}
									</span>
								</td>

								<td>
									{isClaimingConversion(row) && (
										<CheckCircledIcon className={styles.icon} />
									)}
								</td>

								<td key={`${row.idv2}-cancelType`}>
									{getCancelTypeLabel(row.cancelType)}
								</td>

								<td key={`${row.idv2}-cancelDate`}>
									{row.dateCancel ?? "不明"}
								</td>

								<td key={`${row.idv2}-visits`}>{renderVisitCount(row)}</td>

								{currentTab === "RERESERVED" && (
									<>
										<td>
											{row.rereservation?.scrapingTargetSiteReserveId ?? ""}
										</td>

										<td>
											<RereservationPriceCell
												rereservation={row.rereservation}
											/>
										</td>

										<td>
											{row.rereservation?.durationMinute
												? `${row.rereservation.durationMinute}分`
												: ""}
										</td>

										<td className={styles.menu_cell}>
											<span>{formatMenusAndCoupons(row)}</span>
										</td>

										<td>
											{row.rereservation?.startTime
												? formatJstDateUntilDayByHyphen(
														convertGraphQLDateToDate(
															row.rereservation.startTime
														)
													)
												: ""}
										</td>

										<td key={`${row.idv2}-staffName`}>
											{row.rereservation?.staffName ?? ""}
										</td>
									</>
								)}

								<td key={`${row.idv2}-billingTo`}>
									<span>{row.name}</span>
								</td>

								<td key={`${row.idv2}-phoneNumber`}>
									{isEditingRow(row.idv2) ? (
										<input
											className={styles.editing_phone_number}
											type="number"
											value={editingRereservationRowMap[row.idv2].phoneNumber}
											onChange={(event) => {
												const newRow: EditingRereservationRow = {
													...editingRereservationRowMap[row.idv2],
													phoneNumber: event.target.value,
												}
												updateEditingRereservationRow(row.idv2, newRow)
											}}
										/>
									) : (
										<span>{row.phoneNumber}</span>
									)}
								</td>

								{(currentTab === "ENCOURAGING_RERESERVATION_NOT_SCHEDULED" ||
									currentTab === "ENCOURAGING_RERESERVATION_SCHEDULED") && (
									<td key={`${row.idv2}-action`}>
										<ActionButton
											rereservationRow={row}
											isEditingRow={isEditingRow(row.idv2)}
											onClickUpdateButton={() => onClickUpdateButton(row.idv2)}
											onClickActionButton={() => onClickActionButton(row.idv2)}
											currentTab={currentTab}
										/>
									</td>
								)}

								{currentTab === "ENCOURAGING_RERESERVATION_NOT_SCHEDULED" &&
									!isClaimingConversion(row) && (
										<td key={`${row.idv2}-edit`}>
											{canEdit(row.rereservationStatus) &&
												(isEditingRow(row.idv2) ? (
													<a
														href="#"
														onClick={() => cancelRereservationRowEdit(row.idv2)}
													>
														編集取消
													</a>
												) : (
													<a
														href="#"
														onClick={() =>
															onClickRereservationRowEdit(row.idv2)
														}
													>
														編集
													</a>
												))}
										</td>
									)}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<p className={styles.result}>{cancels.length}件の結果</p>
		</div>
	)
}

export default CancelsForRereservationTableView
